import React from 'react';
import styled from 'styled-components/macro';
import { Link, useParams } from 'react-router-dom';
import {
  AmountDisplay,
  FiatValueDisplay,
  useClaimRewardForm,
  RatioAsPercentage,
  useHoldings,
} from '@onomy/react-hub';

import ValidatorFooter from './ValidatorFooter';
import ValidatorNodeHeader from './ValidatorNodeHeader';
import * as Modal from '../styles';
import BackButton from './BackButton';
import StakingModal from './StakingModal';
import StakingTransactionFailedModal from './StakingTransactionFailedModal';
import { responsive } from 'theme/constants';
import LoadingSpinner from 'components/UI/LoadingSpinner';
import { SymbolDisplay } from 'components/Exchange/exchangeStyles';

const DelegateWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const DelegateItem = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: space-between;

  width: 50%;

  @media screen and (max-width: ${responsive.smartphoneLarge}) {
    flex-direction: column;
  }

  & + & {
    padding-left: 55px;

    border-left: 1px solid ${props => props.theme.colors.bgHighlightBorder};
  }
`;

const DelegateItemContent = styled.div<{
  reward?: boolean;
}>`
  display: flex;
  flex-direction: column;
  gap: 4px;

  > span {
    font-size: 14px;
    font-weight: 500;
    color: #9595a6;
  }

  > strong {
    margin-top: 16px;

    font-family: Bebas Neue, sans-serif;
    font-size: 40px;
    font-weight: 600;
    color: ${props =>
      props.reward ? props.theme.colors.highlightBlue : props.theme.colors.textPrimary};
    word-break: break-word;

    > sup {
      margin-left: 6px;
      font-size: 18px;
      word-break: normal;
    }
  }
`;

const Footer = styled.footer`
  display: flex;
  flex-wrap: wrap;
  gap: 24px 100px;

  padding: 32px 0;
  margin-top: 40px;

  border-top: 1px solid ${props => props.theme.colors.bgHighlightBorder};

  @media screen and (max-width: ${responsive.smartphoneLarge}) {
    flex-direction: column;
    flex-wrap: nowrap;
    gap: 16px;
  }
`;

const FooterInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  width: calc(50% - 50px);

  @media screen and (max-width: ${responsive.smartphoneLarge}) {
    width: auto;
  }

  > span {
    font-size: 14px;
    font-weight: 400;
    color: ${props => props.theme.colors.textThirdly};
  }

  > strong {
    font-size: 14px;
    font-weight: 600;
    color: ${props => props.theme.colors.textPrimary};
  }
`;

export default function ValidatorDetail() {
  const { validatorId = '' } = useParams();
  const { nomAvailable, nomUnbonding, nomStaked } = useHoldings();
  const {
    delegation,
    loadingStatus,
    txStatus,
    submit: doClaimReward,
  } = useClaimRewardForm({
    validatorAddressStr: validatorId,
  });

  if (loadingStatus.pending) {
    return (
      <StakingModal>
        <LoadingSpinner />
      </StakingModal>
    );
  }

  if (loadingStatus.error) {
    return <StakingTransactionFailedModal error={loadingStatus.error} />;
  }

  if (txStatus.error) return <StakingTransactionFailedModal error={txStatus.error} />;
  if (!delegation) return <StakingModal>No Matching Validator</StakingModal>;

  const { validator, reward, delegated } = delegation;
  const { votingPowerRatio } = validator;

  return (
    <StakingModal>
      <Modal.StakingWrapper>
        <Link to="/validators/">
          <BackButton />
        </Link>
        <ValidatorNodeHeader delegation={delegation} />

        <DelegateWrapper>
          <DelegateItem>
            <DelegateItemContent reward>
              <span>Delegated</span>
              <strong>
                <AmountDisplay balance={delegated} />
              </strong>
              <span>
                <FiatValueDisplay value={delegated.fiatValue} />
              </span>
            </DelegateItemContent>
          </DelegateItem>
          <DelegateItem>
            <DelegateItemContent reward>
              <span>Reward</span>
              <strong>
                <AmountDisplay balance={reward} />
              </strong>
              <span>
                <FiatValueDisplay value={reward.fiatValue} />
              </span>
            </DelegateItemContent>
            {txStatus.pending ? (
              <LoadingSpinner />
            ) : (
              <Modal.PrimaryButton type="button" style={{ width: '110px' }} onClick={doClaimReward}>
                Claim
              </Modal.PrimaryButton>
            )}
          </DelegateItem>
        </DelegateWrapper>

        <Footer>
          <FooterInfo>
            <span>Commission</span>
            <strong>
              {validator.commissionRate}
              <sup>%</sup>
            </strong>
          </FooterInfo>
          <FooterInfo>
            <span>Voting Power</span>
            <strong>
              <RatioAsPercentage ratio={votingPowerRatio} />
            </strong>
          </FooterInfo>
          <FooterInfo>
            <span>Available</span>
            <strong>
              <AmountDisplay balance={nomAvailable} SymbolComponent={SymbolDisplay} />
            </strong>
          </FooterInfo>
          <FooterInfo>
            <span>Staked</span>
            <strong>
              <AmountDisplay balance={nomStaked} SymbolComponent={SymbolDisplay} />
            </strong>
          </FooterInfo>
          <FooterInfo>
            <span>Locked</span>
            <strong>
              <AmountDisplay balance={nomUnbonding} SymbolComponent={SymbolDisplay} />
            </strong>
          </FooterInfo>
        </Footer>
      </Modal.StakingWrapper>
      <ValidatorFooter>
        <Link to={`/validators/${validatorId}/undelegate`}>
          <Modal.SecondaryButton type="button">Undelegate</Modal.SecondaryButton>
        </Link>
        <Link to={`/validators/${validatorId}/delegate`}>
          <Modal.PrimaryButton type="button">Delegate</Modal.PrimaryButton>
        </Link>
      </ValidatorFooter>
    </StakingModal>
  );
}
